/**
 * Frames sent while the socket is still connecting.
 *
 * `WebSocket.send` throws `InvalidStateError` in the CONNECTING state (unlike
 * CLOSING/CLOSED, where it silently discards). The chat socket is replaced on
 * every reconnect and stored before it opens, and the reconnect is triggered by
 * the same `visibilitychange` that other listeners react to by sending — the
 * location "fix on open" and the read-dwell timer. Prod 2026-09-11→13: six
 * uncaught `Still in CONNECTING state` errors from those two callers, and the
 * frame (a location fix, a read mark) was lost with it.
 *
 * Every frame those callers send states something the user did or saw, which
 * is still true a moment later, so a frame for a CONNECTING socket is held and
 * released — in order — when THAT socket opens. Held frames belong to their
 * socket: if it closes without opening they are dropped with it, exactly as a
 * send with no socket at all is dropped today.
 */

/** The part of a WebSocket this needs; a structural type so tests can fake it. */
export interface FrameSocket {
  readonly readyState: number;
  send(data: string): void;
}

// Numeric readyState values from the WebSocket spec — identical to
// `WebSocket.CONNECTING` / `WebSocket.OPEN`, spelled out so this module has no
// dependency on a browser global.
const CONNECTING = 0;
const OPEN = 1;

export type HeldFrames = WeakMap<FrameSocket, string[]>;

export type SendOutcome = "sent" | "held" | "dropped";

/** Send `frame` now if the socket is open, hold it if the socket is still
 *  connecting, and drop it otherwise (no socket, closing, or closed). */
export const sendOrHold = (socket: FrameSocket | null, frame: string, held: HeldFrames): SendOutcome => {
  if (!socket) return "dropped";
  if (socket.readyState === OPEN) {
    socket.send(frame);
    return "sent";
  }
  if (socket.readyState === CONNECTING) {
    held.set(socket, [...(held.get(socket) ?? []), frame]);
    return "held";
  }
  return "dropped";
};

/** Send, in order, everything held for this now-open socket. Returns the count. */
export const releaseHeld = (socket: FrameSocket, held: HeldFrames): number => {
  const frames = held.get(socket) ?? [];
  held.delete(socket);
  frames.forEach((frame) => { socket.send(frame); });
  return frames.length;
};
